/* eslint-disable no-unreachable */
import { useMutation, useLazyQuery } from "@apollo/client";
import { Button, Form, Input, Radio, message } from "antd";
import { PlusCircleFilled } from "@ant-design/icons";
import { capitalize } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_LEAD_V2 } from "../../../graphql/mutation";
import { GET_LEAD_DETAIL_CLICK } from "../../../graphql/query";
import { updateLeadInList } from "../../../redux/slices/leadSlice";
import AdditionalEmailForm from "../common/AdditionalEmailForm";

export const EmailForm = ({ emailRef, setSelectedLead, emails = [], onClose }) => {
  const [form] = Form.useForm();
  const emailTypes = ["PERSONAL", "WORK", "OTHER"];
  // primaryEmailType can be: "PERSONAL", "WORK", "OTHER" (for self emails) or "additional_0", "additional_1", etc. (for additional emails)
  const [primaryEmailType, setprimaryEmailType] = useState(emailTypes[0]);
  const [isSaving, setIsSaving] = useState(false);

  // Separate "Self" emails from additional emails
  const selfEmails = emails.filter(
    email => !email.relationshipType || email.relationshipType === "Self"
  );
  const additionalEmailsData = emails.filter(
    email => email.relationshipType && email.relationshipType !== "Self"
  );

  const [emailData, setemailData] = useState(
    selfEmails.map(email => ({
      ...email,
      emailType: email.emailType?.toUpperCase() || emailTypes[0],
    }))
  );
  const [additionalEmails, setAdditionalEmails] = useState(
    additionalEmailsData.map((email, index) => ({
      id: email.id || Math.random(),
      email: email.email,
      emailType: email.emailType,
      relation: email.relationshipType,
      firstName: email.firstName || "",
      lastName: email.lastName || "",
      note: email.note || "",
      isDeleted: false,
    }))
  );
  const dispatch = useDispatch();
  const close = () => {
    if (onClose) {
      onClose();
    } else {
      emailRef.current.click();
    }
  };
  const { selectedLead } = useSelector(state => state.lead);
  const [getLeadById] = useLazyQuery(GET_LEAD_DETAIL_CLICK);
  const [updateLeadInformation, { loading: updateLeadLoader }] = useMutation(UPDATE_LEAD_V2);
  // Function to collect additional emails from form
  const collectAdditionalEmails = () => {
    const formValues = form.getFieldsValue();
    const additionalEmailsData = [];

    additionalEmails.forEach((emailItem, index) => {
      // If item is marked as deleted, send complete object with isDeleted: true
      if (emailItem.isDeleted) {
        // Only include if it's an existing record from database
        if (emailItem.id && !emailItem.id.toString().startsWith("0.")) {
          additionalEmailsData.push({
            id: emailItem.id,
          });
        }
      } else {
        // For non-deleted items, get current form values
        const email = formValues[`additional_email_${index}`];
        const emailType = formValues[`additional_email_type_${index}`];
        const relation = formValues[`additional_email_relation_${index}`];
        const firstName = formValues[`additional_email_firstName_${index}`];
        const lastName = formValues[`additional_email_lastName_${index}`];
        const note = formValues[`additional_email_note_${index}`];

        if (email) {
          // Check if this additional email is the primary one
          const isThisPrimary = primaryEmailType === `additional_${index}`;
          const emailData = {
            email,
            emailType,
            relationshipType: relation || "Other",
            firstName,
            lastName,
            note,
            isPrimary: isThisPrimary,
          };
          // Include id if it exists (for updating existing records)
          if (emailItem.id && !emailItem.id.toString().startsWith("0.")) {
            emailData.id = emailItem.id;
          }
          additionalEmailsData.push(emailData);
        }
      }
    });

    return additionalEmailsData;
  };

  const saveEmail = async value => {
    if (isSaving) return; // Prevent multiple clicks

    // Collect additional emails
    const collectedAdditionalEmails = collectAdditionalEmails();

    // Edge case: primary radio points to an empty row but other rows have values.
    {
      let primaryHasValue = false;
      if (emailTypes.includes(primaryEmailType)) {
        const se = emailData.find(e => e.emailType === primaryEmailType);
        primaryHasValue = !!se && !!se.email && se.email.trim() !== "";
      } else if (primaryEmailType?.startsWith?.("additional_")) {
        const idx = parseInt(primaryEmailType.replace("additional_", ""), 10);
        const v = form.getFieldValue(`additional_email_${idx}`);
        primaryHasValue = !!v && v.trim() !== "";
      }
      if (!primaryHasValue) {
        const anyHasValue =
          emailData.some(e => e.email && e.email.trim() !== "") ||
          collectedAdditionalEmails.some(e => e.email && e.email.trim() !== "");
        if (anyHasValue) {
          return message.error(
            "The email marked as primary is empty. Please enter a value or mark a non-empty email as primary."
          );
        }
      }
    }

    // Merge self emails with additional emails
    // Check if primary is a self email type or an additional email
    const isPrimarySelfEmail = emailTypes.includes(primaryEmailType);
    const allEmails = [
      ...emailData.map(item => {
        const emailData = {
          email: item.email,
          emailType: item.emailType,
          // Self email is primary only if primaryEmailType matches AND it's not an additional email
          isPrimary: isPrimarySelfEmail && item.emailType === primaryEmailType,
          relationshipType: "Self",
          firstName: item.firstName || "",
          lastName: item.lastName || "",
          note: item.note || "",
        };
        // Include id if it exists (for updating existing records)
        if (item.id && !item.id.toString().startsWith("0.")) {
          emailData.id = item.id;
        }
        return emailData;
      }),
      ...collectedAdditionalEmails,
    ];

    setIsSaving(true);
    try {
      const res = await updateLeadInformation({
        variables: {
          firstName: selectedLead?.firstName,
          lastName: selectedLead?.lastName,
          leadAddressObj:
            selectedLead?.addresses.length > 0
              ? {
                  addressLine1: selectedLead?.addresses[0].addressLine1,
                  addressLine2: selectedLead?.addresses[0].addressLine2,
                  city: selectedLead?.addresses[0].city,
                  state: selectedLead?.addresses[0].state,
                  country: selectedLead?.addresses[0].country,
                  postalCode: selectedLead?.addresses[0].postalCode,
                  locationText: "Main Branch",
                }
              : null,
          leadId: selectedLead.id,
          emails: allEmails,
          isSyncCheck: false,
          isPushCrm: false,
        },
      });

      if (res.data.updateLead.statusCode === 200) {
        message.success(res.data.updateLead.message);

        try {
          const leadRes = await getLeadById({
            variables: {
              leadId: selectedLead.id,
            },
            fetchPolicy: "network-only",
          });

          if (leadRes?.data?.lead) {
            // Update selectedLead with new email data
            const updatedLead = {
              ...selectedLead,
              emails: leadRes?.data?.lead?.emails,
            };
            setSelectedLead(updatedLead);
            dispatch(updateLeadInList(updatedLead));
            close();
          } else {
            console.error("getLeadById returned no data");
            close();
          }
        } catch (error) {
          console.error("Error fetching updated lead data:", error);
          // Close drawer even if getLeadById fails
          close();
        }
      } else {
        message.error(res.data.updateLead.message);
      }
    } catch (error) {
      console.error("Error updating email:", error);
      message.error("Failed to update email addresses");
    } finally {
      setIsSaving(false);
    }
  };
  useEffect(() => {
    // Update emailData state with all self emails
    // Normalize emailType to uppercase to match form field names (PERSONAL, WORK, OTHER)
    const updatedEmailData = selfEmails.map(email => ({
      ...email,
      emailType: email.emailType?.toUpperCase() || emailTypes[0],
    }));
    setemailData(updatedEmailData);

    // Update additionalEmails state when emails prop changes (important for edit mode)
    const updatedAdditionalEmails = additionalEmailsData.map(email => ({
      id: email.id || Math.random(),
      email: email.email,
      emailType: email.emailType,
      relation: email.relationshipType,
      firstName: email.firstName || "",
      lastName: email.lastName || "",
      note: email.note || "",
      isPrimary: email.isPrimary || false,
      isDeleted: false,
    }));
    setAdditionalEmails(updatedAdditionalEmails);

    // Set primary email type - check both self emails and additional emails
    const primarySelfEmail = selfEmails.find(item => item.isPrimary);
    if (primarySelfEmail) {
      // Normalize to uppercase to match emailTypes array
      setprimaryEmailType(primarySelfEmail.emailType?.toUpperCase() || emailTypes[0]);
    } else {
      // Check if any additional email is primary
      const primaryAdditionalIndex = additionalEmailsData.findIndex(item => item.isPrimary);
      if (primaryAdditionalIndex !== -1) {
        setprimaryEmailType(`additional_${primaryAdditionalIndex}`);
      } else if (selfEmails.length > 0) {
        // Default to first self email type if no primary is set (normalize to uppercase)
        setprimaryEmailType(selfEmails[0]?.emailType?.toUpperCase() || emailTypes[0]);
      }
    }

    // Populate additional email fields
    if (additionalEmailsData.length > 0) {
      const additionalEmailFormValues = {};
      additionalEmailsData.forEach((email, index) => {
        additionalEmailFormValues[`additional_email_${index}`] = email.email;
        additionalEmailFormValues[`additional_email_type_${index}`] = email.emailType;
        additionalEmailFormValues[`additional_email_relation_${index}`] = email.relationshipType;
        additionalEmailFormValues[`additional_email_firstName_${index}`] = email.firstName || "";
        additionalEmailFormValues[`additional_email_lastName_${index}`] = email.lastName || "";
        additionalEmailFormValues[`additional_email_note_${index}`] = email.note || "";
      });
      form.setFieldsValue(additionalEmailFormValues);
    }
  }, [emails]);
  const getEmail = type => {
    const email = emailData.find(email => email.emailType === type);
    return email;
  };
  const serializeEmail = input => {
    const result = {};

    // Iterate over each object in the array
    input.forEach(item => {
      // Get the key of the current object (e.g., "Mobile" or "Work")
      const key = Object.keys(item)[0];
      // Assign the value to the result object
      result[item[key]] = item.email;
    });
    return result;
  };
  useEffect(() => {
    form.setFieldsValue(
      serializeEmail(emailData?.map(item => ({ emailType: item.emailType, email: item.email })))
    );
  }, [emailData]);
  const setEmail = (value, type) => {
    const index = emailData.findIndex(email => email.emailType === type);
    if (index !== -1) {
      setemailData(prev => {
        const temp = structuredClone(prev);
        temp[index] = {
          ...temp[index],
          email: value,
        };
        return temp;
      });
    } else {
      return setemailData([...emailData, { email: value, emailType: type }]);
    }
  };
  // Helper to get additional email value from form
  const getAdditionalEmailValue = index => {
    const formValues = form.getFieldsValue();
    return formValues[`additional_email_${index}`];
  };

  return (
    <Form layout="vertical" className="w-full" form={form} onFinish={saveEmail}>
      <Radio.Group
        value={primaryEmailType}
        onChange={e => {
          const selectedValue = e.target.value;

          // Check if it's a self email type or additional email
          if (emailTypes.includes(selectedValue)) {
            // Self email type
            const emailValue = getEmail(selectedValue)?.email;
            if (!emailValue || emailValue.trim() === "") {
              message.warning(
                `Please enter a ${capitalize(selectedValue)} email before marking it as primary`
              );
              return;
            }
          } else if (selectedValue.startsWith("additional_")) {
            // Additional email
            const index = parseInt(selectedValue.replace("additional_", ""), 10);
            const emailValue = getAdditionalEmailValue(index);
            if (!emailValue || emailValue.trim() === "") {
              message.warning("Please enter an email before marking it as primary");
              return;
            }
          }

          setprimaryEmailType(selectedValue);
        }}
        className="w-full"
      >
        {/* Main Email Section */}
        <div className="bg-gray-50 p-3 rounded-md mb-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Email Addresses</h3>
            <span className="text-xs text-gray-500">Select Primary</span>
          </div>

          {emailTypes.map((item, index) => (
            <div key={index} className="w-full mb-3 last:mb-0 flex items-center gap-2">
              <Form.Item
                rules={[
                  {
                    required: primaryEmailType === item,
                    message: `Please enter the ${capitalize(item)} email`,
                  },
                  { type: "email", message: `Enter a valid ${capitalize(item)} email` },
                ]}
                label={
                  <span className="text-xs font-medium text-gray-600">
                    Email ({capitalize(item)})
                  </span>
                }
                name={`${item}`}
                className="mb-0 flex-1"
                style={{ marginBottom: 0, width: "100%" }}
              >
                <Input
                  className="flex-1"
                  value={getEmail(item)?.email}
                  onChange={e => setEmail(e.target.value, item)}
                  name={`${item}`}
                  placeholder={`Enter ${capitalize(item)} email`}
                />
              </Form.Item>
              <Radio value={item} className="flex-shrink-0 mt-9" />
            </div>
          ))}
        </div>

        <h3 className="font-semibold text-gray-700 mb-3 py-2 border-b border-gray-300">
          Additional Emails
        </h3>
        {/* Additional Emails Section */}
        {additionalEmails.filter(email => !email.isDeleted).length > 0 && (
          <div className="mb-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="space-y-3">
              {additionalEmails.map((emailItem, index) => {
                if (emailItem.isDeleted) return null;
                return (
                  <AdditionalEmailForm
                    key={`email_key__${index}`}
                    item={emailItem}
                    index={index}
                    onDelete={() => {
                      if (primaryEmailType === `additional_${index}`) {
                        message.error(
                          "This email is marked as primary. Please mark another email as primary before deleting."
                        );
                        return;
                      }
                      setAdditionalEmails(prev => {
                        const itemToDelete = prev.find(item => item.id === emailItem.id);
                        // If item has a numeric id (from database), mark as deleted
                        // Otherwise just filter it out (it's a new item not yet saved)
                        if (itemToDelete && !itemToDelete.id.toString().startsWith("0.")) {
                          return prev.map(item =>
                            item.id === emailItem.id ? { ...item, isDeleted: true } : item
                          );
                        }
                        return prev.filter(item => item.id !== emailItem.id);
                      });
                    }}
                    showPrimary
                    primaryValue={`additional_${index}`}
                    isPrimary={primaryEmailType === `additional_${index}`}
                  />
                );
              })}
            </div>
          </div>
        )}
      </Radio.Group>
      {/* Add Additional Email Button */}
      <div
        className="flex w-[200px] items-center justify-center gap-2 p-3 mb-4 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
        onClick={() => {
          setAdditionalEmails(prev => {
            const newState = [...prev];
            newState.push({
              id: Math.random(),
              email: "",
              emailType: "",
              relation: "",
              firstName: "",
              lastName: "",
              note: "",
              isDeleted: false,
            });
            return newState;
          });
        }}
      >
        <PlusCircleFilled className="text-blue-500" />
        <span className="text-sm font-medium text-blue-500">Add Additional Email</span>
      </div>
      {/* Action Buttons */}
      <div className="flex gap-2 pt-3 border-t border-gray-200">
        <Button type="primary" loading={isSaving || updateLeadLoader} htmlType="submit">
          Save Changes
        </Button>
        <Button onClick={close} disabled={isSaving}>
          Cancel
        </Button>
      </div>
    </Form>
  );
};
